import { useEffect, useRef, useState } from 'react';
import { Check, CircleAlert, Loader, X } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import { Button, Field, FilePicker, Input, Select } from '../../components/ui';
import { useCreateCharacter, useCreateVoice, useUploadAvatar } from '../../hooks';
import {
  CHARACTER_CATEGORIES,
  CHARACTER_CATEGORY_LABELS,
  type Character,
  type CharacterCategory,
  type Id,
} from '../../types/api';
import { LANGUAGE_OPTIONS } from './lib';
import styles from './Characters.module.css';

interface Props {
  onClose: () => void;
  onCreated: (characterId: Id) => void;
}

export function AddCharacterDialog({ onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('ru');
  const [category, setCategory] = useState<CharacterCategory | ''>('');
  const [video, setVideo] = useState<File | null>(null);
  const [sample, setSample] = useState<File | null>(null);
  const [created, setCreated] = useState<Character | null>(null);

  const create = useCreateCharacter();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !create.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, create.isPending]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate(
      { name: trimmed, language, category: category || null },
      { onSuccess: (character) => setCreated(character) },
    );
  };

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Новый персонаж">
      <div className={styles.dialog}>
        <div className={styles.dialogHead}>
          <span className={styles.dialogTitle}>
            {created ? 'Персонаж создан' : 'Новый персонаж'}
          </span>
          <Button
            size="sm"
            iconOnly
            variant="ghost"
            className={styles.pushRight}
            onClick={onClose}
            aria-label="Закрыть"
            disabled={create.isPending}
          >
            <X size={14} strokeWidth={1.6} />
          </Button>
        </div>

        {created ? (
          <AssetsStep
            character={created}
            video={video}
            sample={sample}
            onDone={() => onCreated(created.id)}
          />
        ) : (
          <>
            <div className={styles.dialogBody}>
              <Field layout="stacked" label="Имя">
                {(id) => (
                  <Input
                    id={id}
                    type="text"
                    value={name}
                    autoFocus
                    maxLength={200}
                    placeholder="Как его называют в работе"
                    onChange={(event) => setName(event.target.value)}
                  />
                )}
              </Field>

              <Field layout="stacked" label="Язык контента">
                {(id) => (
                  <Select
                    id={id}
                    value={language}
                    onChange={(event) => setLanguage(event.target.value)}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field layout="stacked" label="Категория">
                {(id) => (
                  <Select
                    id={id}
                    value={category}
                    onChange={(event) => setCategory(event.target.value as CharacterCategory | '')}
                  >
                    <option value="">не указан</option>
                    {CHARACTER_CATEGORIES.map((value) => (
                      <option key={value} value={value}>
                        {CHARACTER_CATEGORY_LABELS[value]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field layout="stacked" label="Видео персонажа">
                <FilePicker
                  file={video}
                  onPick={setVideo}
                  accept="video/*,image/*"
                  emptyLabel="видео не выбрано"
                  ariaLabel="Видео персонажа"
                />
              </Field>

              <Field layout="stacked" label="Образец голоса">
                <FilePicker
                  file={sample}
                  onPick={setSample}
                  accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac"
                  emptyLabel="образец не выбран"
                  ariaLabel="Образец голоса"
                />
              </Field>

              {create.error && <ErrorBanner error={create.error} />}
            </div>

            <div className={styles.dialogFoot}>
              <Button onClick={onClose} disabled={create.isPending}>
                Отмена
              </Button>
              <Button
                variant="primary"
                onClick={submit}
                disabled={create.isPending || name.trim().length === 0}
              >
                {create.isPending ? 'Создаём…' : 'Создать'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface AssetsProps {
  character: Character;
  video: File | null;
  sample: File | null;
  onDone: () => void;
}

function AssetsStep({ character, video, sample, onDone }: AssetsProps) {
  const upload = useUploadAvatar(character.id);
  const createVoice = useCreateVoice();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (video) upload.mutate(video);
    if (sample) {
      createVoice.mutate({
        character_id: character.id,
        sample,
        name: character.name,
        make_current: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = upload.isPending || createVoice.isPending;
  const failed = upload.isError || createVoice.isError;

  // Одна строка вместо трёх. Раньше здесь висели крутилки у каждого файла, причём
  // у образца голоса — над примечанием, которое ничего не выполняет: окно выглядело
  // зависшим. Кнопка больше не блокируется: загрузка живёт в мутации, а не в окне,
  // и закрытие её не прерывает.
  const what = [video && 'видео', sample && 'образец голоса'].filter(Boolean).join(' и ');

  return (
    <>
      <div className={styles.dialogBody}>
        <StepLine label="Карточка персонажа" state="done" note={`«${character.name}» заведён`} />

        {what && (
          <StepLine
            label={busy ? `Загружаем ${what}` : failed ? 'Загрузка не удалась' : 'Файлы загружены'}
            state={busy ? 'running' : failed ? 'failed' : 'done'}
            note={busy ? 'окно можно закрыть, загрузка продолжится' : ''}
          />
        )}

        {upload.error && <ErrorBanner error={upload.error} />}
        {createVoice.error && <ErrorBanner error={createVoice.error} />}
      </div>

      <div className={styles.dialogFoot}>
        <Button variant="primary" onClick={onDone}>
          Открыть карточку
        </Button>
      </div>
    </>
  );
}

function StepLine({
  label,
  state,
  note,
}: {
  label: string;
  state: 'done' | 'running' | 'failed' | 'skipped';
  note: string;
}) {
  return (
    <div className={styles.progressLine}>
      {state === 'done' && <Check size={14} strokeWidth={2} className={styles.iconOk} />}
      {state === 'running' && <Loader size={14} strokeWidth={1.6} />}
      {state === 'failed' && (
        <CircleAlert size={14} strokeWidth={1.6} className={styles.iconFail} />
      )}
      {state === 'skipped' && <span className={styles.stepSpacer} />}
      <span>{label}</span>
      <span className={styles.progressNote} title={note}>
        {note}
      </span>
    </div>
  );
}
